import React, { Fragment, useState, useCallback, useEffect } from 'react';
import { ReferenceManyField, useTranslate } from 'react-admin';
import Grid from '@material-ui/core/Grid';
import {
  TextInput,
  PaymentInstrumentSelect,
  SelectInput,
  ProfileTagsFilterInput,
} from '~/components';
import { PricingShow, PricingCreate } from '../Pricing';
import { PricingDatagrid } from '../Pricing/PricingDatagrid';
import RaPagination from '~/components/Pagination/RaPagination';
import { rowsPerPageOptionsDefault, perPageDefault } from '~/utils/pagination';
import makeStyles from '@material-ui/core/styles/makeStyles';
import usePath from '~/hooks/usePath';
import { debounce, set as lodashSet } from 'lodash';
import { useHandbooksQuery } from '~/hooks/useHandbook';
import { useSelector } from 'react-redux';
import lodashGet from 'lodash/get';
import { Pricing } from '~/types';

interface PricingTabProps {
  record?: any;
  permissions?: any;
  [x: string]: any;
}

const PricingTab = (props: PricingTabProps) => {
  const { record, permissions, ...rest } = props;
  const classes = useStyles();

  const [filters, setFilters] = useState<any>({});
  const [showData, setShowData] = useState<Pricing | null>(null);
  const handleOpen = (data: any) => {
    permissions.includes('solution.pricing.view') && setShowData(data);
  };
  const handleClose = () => setShowData(null);

  const pricingFromRedux = useSelector((state: any) =>
    lodashGet(state, `admin.resources.pricings.data[${showData?.id}]`)
  );
  useEffect(() => {
    setShowData(pricingFromRedux);
  }, [pricingFromRedux]);

  return (
    <Fragment>
      <PricingShow
        record={showData}
        permissions={permissions}
        solutionRecord={record}
        onClose={handleClose}
        {...rest}
      />
      <Grid container justify='space-between' alignItems='center' className={classes.spacings}>
        <Grid item>
          <Filters {...props} filters={filters} setFilters={setFilters} solutionId={record?.id} />
        </Grid>
        <Grid item id='section-actions'>
          <PricingCreate permissions={permissions} solutionRecord={record} />
        </Grid>
      </Grid>
      <ReferenceManyField
        label='Configurations'
        reference='pricings'
        target='solutions'
        sort={{ field: 'name', order: 'ASC' }}
        pagination={
          <RaPagination
            defaultPerPage={perPageDefault}
            rowsPerPageOptions={rowsPerPageOptionsDefault}
          />
        }
        perPage={perPageDefault}
        filter={filters}
        {...props}
      >
        <PricingDatagrid
          rowClick={(_id: any, _basePath: any, record: any) => handleOpen(record)}
          showLinkToPricing
        />
      </ReferenceManyField>
    </Fragment>
  );
};

const Filters = (props: any) => {
  const { filters, setFilters, solutionId } = props;
  const classes = useStyles();
  const translate = useTranslate();
  const t = (key: string) => translate(`resources.solutions.pricings.${key}`);
  const td = (key: string) => translate(`utils.${key}`);

  const { changePath } = usePath();
  const debouncedSetFilters = useCallback(debounce(setFilters, 400), [setFilters]);
  const handleChange = (e: any) => {
    if (!e?.target) return null;
    const { name, value } = e.target;
    changePath({ page: 1 });
    debouncedSetFilters((prev: any) => {
      return { ...lodashSet(prev, name, value) };
    });
  };

  const { data: handbook } = useHandbooksQuery(solutionId);
  const kycLevelOptions =
    handbook?.kycLevels?.map((level) => ({
      value: level?.id,
      text: level?.name,
    })) ?? [];

  const handleArrayChange = (name: string) => (_event: any, value: any) => {
    changePath({ page: 1 });
    debouncedSetFilters((prev: any) => {
      return { ...lodashSet(prev, name, value) };
    });
  };

  return (
    <form className={classes.filters}>
      <TextInput
        name='name'
        source='name'
        label={t('name')}
        onChange={handleChange}
        className={classes.formControl}
      />
      <PaymentInstrumentSelect
        solutionId={solutionId}
        name='paymentInstrument'
        source='paymentInstrument'
        label={t('paymentInstrument')}
        value={filters.paymentInstrument ?? ''}
        emptyText={td('any')}
        onChange={handleChange}
        className={classes.formControl}
        disableHelperText
        allowEmpty
      />
      <SelectInput
        options={kycLevelOptions as any}
        name='kycLevel'
        source='kycLevel'
        label={t('kycLevel')}
        value={filters.kycLevel ?? ''}
        emptyText={td('any')}
        onChange={handleChange}
        className={classes.formControl}
        disableHelperText
        allowEmpty
      />
      <ProfileTagsFilterInput
        value={filters?.profileTags}
        onChange={handleArrayChange('profileTags')}
        label={t('profileTags')}
        className={classes.formControlLong}
      />
    </form>
  );
};

const useStyles = makeStyles((theme) => ({
  spacings: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  filters: {
    alignItems: 'flex-start',
    display: 'flex',
    justifyContent: 'space-between',
  },
  formControl: {
    marginRight: theme.spacing(1),
    minWidth: 150,
    maxWidth: 270,
    '&:last-child': {
      marginRight: 0,
    },
  },
  formControlLong: {
    marginRight: theme.spacing(1),
    minWidth: 300,
    maxWidth: 300,
    '&:last-child': {
      marginRight: 0,
    },
  },
}));

export default PricingTab;
